import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  MultiSelect,
  Select,
  Button,
  Group,
  Card,
  Badge,
  Checkbox,
  Divider,
  LoadingOverlay,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Competency, Grade, Swimlane } from '../../types';
import { useCompetencies, useAddCompetenciesToMap } from '../../hooks/useApi';
import { showToast } from '../../utils/toast';

interface ExistingCompetenciesSelectorProps {
  competencyMapId: string;
  grades: Grade[];
  swimlanes: Swimlane[];
  onClose: () => void;
  onSuccess: () => void;
  preselectedCompetency?: Competency | null;
}

interface SelectedCompetency {
  competency: Competency;
  gradeId: string;
  swimlaneId: string;
  isRequired: boolean;
}

export const ExistingCompetenciesSelector: React.FC<ExistingCompetenciesSelectorProps> = ({
  competencyMapId,
  grades,
  swimlanes,
  onClose,
  onSuccess,
  preselectedCompetency,
}) => {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';
  const [selectedCompetencies, setSelectedCompetencies] = useState<SelectedCompetency[]>([]);
  const [availableCompetencies, setAvailableCompetencies] = useState<Competency[]>([]);

  // Получаем все компетенции
  const { data: allCompetencies = [], isLoading: isLoadingCompetencies } = useCompetencies();
  const addCompetenciesMutation = useAddCompetenciesToMap();

  // Фильтруем компетенции, которых нет в текущей карте
  useEffect(() => {
    if (allCompetencies.length > 0) {
      // Получаем компетенции текущей карты (удаляем дубликаты)
      const currentMapCompetencies = grades.flatMap(
        (grade) => grade.competencies?.map((comp) => comp._id) || [],
      );
      const uniqueCurrentMapCompetencies = Array.from(new Set(currentMapCompetencies));

      // Находим дубликаты и выводим их названия
      const duplicates = currentMapCompetencies.filter(
        (item, index) => currentMapCompetencies.indexOf(item) !== index,
      );

      if (duplicates.length > 0) {
        console.log('=== ДУБЛИКАТЫ КОМПЕТЕНЦИЙ ===');
        duplicates.forEach((duplicateId) => {
          const competency = allCompetencies.find((comp) => comp._id === duplicateId);
          if (competency) {
            console.log(`ID: ${duplicateId}, Название: ${competency.title}`);
          }
        });

        // Показываем в каких грейдах находятся дубликаты
        console.log('=== РАСПОЛОЖЕНИЕ ДУБЛИКАТОВ ===');
        duplicates.forEach((duplicateId) => {
          const competency = allCompetencies.find((comp) => comp._id === duplicateId);
          if (competency) {
            const gradesWithDuplicate = grades.filter((grade) =>
              grade.competencies?.some((comp) => comp._id === duplicateId),
            );
            console.log(
              `"${competency.title}" находится в грейдах:`,
              gradesWithDuplicate.map((g) => g.title).join(', '),
            );
          }
        });
        console.log('=== КОНЕЦ ДУБЛИКАТОВ ===');
      }

      // Фильтруем компетенции, которых нет в текущей карте
      const filteredCompetencies = allCompetencies.filter(
        (comp) => !uniqueCurrentMapCompetencies.includes(comp._id),
      );

      setAvailableCompetencies(filteredCompetencies);
    }
  }, [allCompetencies, grades]);

  // Автоматический выбор предварительно выбранной компетенции
  useEffect(() => {
    if (preselectedCompetency && availableCompetencies.length > 0) {
      const isAlreadySelected = selectedCompetencies.some(
        (sc) => sc.competency._id === preselectedCompetency._id,
      );

      if (!isAlreadySelected) {
        const newSelectedCompetency: SelectedCompetency = {
          competency: preselectedCompetency,
          gradeId: grades[0]?._id || '',
          swimlaneId: swimlanes[0]?._id || '',
          isRequired: false,
        };

        setSelectedCompetencies([newSelectedCompetency]);
      }
    }
  }, [preselectedCompetency, availableCompetencies, grades, swimlanes, selectedCompetencies]);

  const handleCompetencySelect = (competencyIds: string[]) => {
    const newSelectedCompetencies: SelectedCompetency[] = [];

    competencyIds.forEach((competencyId) => {
      const competency = availableCompetencies.find((c) => c._id === competencyId);
      if (competency) {
        // Проверяем, не выбран ли уже этот компетенция
        const alreadySelected = selectedCompetencies.find(
          (sc) => sc.competency._id === competencyId,
        );
        if (!alreadySelected) {
          newSelectedCompetencies.push({
            competency,
            gradeId: grades[0]?._id || '',
            swimlaneId: swimlanes[0]?._id || '',
            isRequired: false,
          });
        }
      }
    });

    // Удаляем компетенции, которые больше не выбраны
    const filteredSelected = selectedCompetencies.filter((sc) =>
      competencyIds.includes(sc.competency._id),
    );

    setSelectedCompetencies([...filteredSelected, ...newSelectedCompetencies]);
  };

  const handleGradeChange = (competencyId: string, gradeId: string) => {
    setSelectedCompetencies((prev) =>
      prev.map((sc) => (sc.competency._id === competencyId ? { ...sc, gradeId } : sc)),
    );
  };

  const handleSwimlaneChange = (competencyId: string, swimlaneId: string) => {
    setSelectedCompetencies((prev) =>
      prev.map((sc) => (sc.competency._id === competencyId ? { ...sc, swimlaneId } : sc)),
    );
  };

  const handleIsRequiredChange = (competencyId: string, isRequired: boolean) => {
    setSelectedCompetencies((prev) =>
      prev.map((sc) => (sc.competency._id === competencyId ? { ...sc, isRequired } : sc)),
    );
  };

  const handleSubmit = async () => {
    try {
      if (selectedCompetencies.length === 0) {
        showToast({
          title: t('common.error'),
          message: t('competencyMaps.selectCompetenciesToAdd'),
          type: 'error',
        });
        return;
      }

      // Проверяем, что у всех компетенций выбраны грейд и свимлейн
      const invalidSelections = selectedCompetencies.filter((sc) => !sc.gradeId || !sc.swimlaneId);

      if (invalidSelections.length > 0) {
        showToast({
          title: t('common.error'),
          message: t('competencyMaps.selectGradeAndSwimlaneForAll'),
          type: 'error',
        });
        return;
      }

      await addCompetenciesMutation.mutateAsync({
        mapId: competencyMapId,
        competencies: selectedCompetencies.map((sc) => ({
          competencyId: sc.competency._id,
          gradeId: sc.gradeId,
          isRequired: sc.isRequired,
          swimlaneId: sc.swimlaneId,
        })),
      });

      showToast({
        title: t('common.success'),
        message: t('competencyMaps.competenciesAddedToMap'),
        type: 'success',
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adding competencies to map:', error);
      showToast({
        title: t('common.error'),
        message: t('competencyMaps.errorAddingCompetencies'),
        type: 'error',
      });
    }
  };

  const isFormValid =
    selectedCompetencies.length > 0 &&
    selectedCompetencies.every((sc) => sc.gradeId && sc.swimlaneId);

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay
        visible={isLoadingCompetencies}
        overlayProps={{ backgroundOpacity: 0.6, fixed: true }}
        loaderProps={{ size: 'sm' }}
      />

      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('competencyMaps.selectExistingCompetencies')}
        </Text>

        <MultiSelect
          label={t('competencyMaps.availableCompetencies')}
          placeholder={t('competencyMaps.selectCompetencies')}
          data={availableCompetencies.map((comp) => ({
            value: comp._id,
            label: comp.title,
          }))}
          value={selectedCompetencies.map((sc) => sc.competency._id)}
          onChange={handleCompetencySelect}
          searchable
          clearable
        />

        {selectedCompetencies.length > 0 && (
          <>
            <Divider />
            <Text fw={500} size="sm">
              {t('competencyMaps.selectedCompetencies')} ({selectedCompetencies.length})
            </Text>

            <Stack gap="sm">
              {selectedCompetencies.map((selected) => (
                <Card key={selected.competency._id} withBorder p="sm">
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text fw={500} size="sm">
                        {selected.competency.title}
                      </Text>
                      {selected.competency.tags && selected.competency.tags.length > 0 && (
                        <Group gap={4}>
                          {selected.competency.tags.slice(0, 2).map((tag) => (
                            <Badge
                              key={tag._id}
                              size="xs"
                              variant={isDark ? 'filled' : 'light'}
                              color={isDark ? 'gray.7' : 'gray'}
                            >
                              {tag.name}
                            </Badge>
                          ))}
                          {selected.competency.tags.length > 2 && (
                            <Badge
                              size="xs"
                              variant={isDark ? 'filled' : 'light'}
                              color={isDark ? 'gray.7' : 'gray'}
                            >
                              +{selected.competency.tags.length - 2}
                            </Badge>
                          )}
                        </Group>
                      )}
                    </Group>

                    {selected.competency.description && (
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {selected.competency.description}
                      </Text>
                    )}

                    <Group gap="sm">
                      <Select
                        label={t('competencyMaps.grade')}
                        placeholder={t('competencyMaps.selectGrade')}
                        data={grades.map((g) => ({ value: g._id, label: g.title }))}
                        value={selected.gradeId}
                        onChange={(value) =>
                          handleGradeChange(selected.competency._id, value || '')
                        }
                        size="xs"
                        style={{ flex: 1 }}
                        required
                      />

                      <Select
                        label={t('competencyMaps.swimlane')}
                        placeholder={t('competencyMaps.selectSwimlane')}
                        data={swimlanes.map((s) => ({ value: s._id, label: s.title }))}
                        value={selected.swimlaneId}
                        onChange={(value) =>
                          handleSwimlaneChange(selected.competency._id, value || '')
                        }
                        size="xs"
                        style={{ flex: 1 }}
                        required
                      />
                    </Group>

                    <Checkbox
                      label={t('competencyMaps.isRequired')}
                      checked={selected.isRequired}
                      onChange={(event) =>
                        handleIsRequiredChange(selected.competency._id, event.currentTarget.checked)
                      }
                      size="xs"
                    />
                  </Stack>
                </Card>
              ))}
            </Stack>
          </>
        )}

        <Group justify="space-between" mt="md">
          <Button variant="light" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            loading={addCompetenciesMutation.isPending}
            disabled={!isFormValid}
          >
            {t('competencyMaps.addToMap')}
          </Button>
        </Group>
      </Stack>
    </div>
  );
};
